import type { EventSummary } from "@/lib/events/types";
import type { LinkPreview } from "@/lib/feed/link-preview";

export const POST_ATTACHMENT_BUCKET = "post-attachments";
export const MAX_ATTACHMENT_BYTES = 15 * 1024 * 1024;
export const FEED_PAGE_SIZE = 20;

export const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
] as const;

export const ALLOWED_VIDEO_TYPES = [
  "video/mp4",
  "video/webm",
  "video/quicktime",
] as const;

export const ALLOWED_DOCUMENT_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.oasis.opendocument.text",
  "application/vnd.oasis.opendocument.spreadsheet",
  "application/vnd.oasis.opendocument.presentation",
  "text/plain",
  "application/rtf",
  "text/rtf",
] as const;

export const ALLOWED_MEDIA_TYPES = [
  ...ALLOWED_IMAGE_TYPES,
  ...ALLOWED_VIDEO_TYPES,
] as const;

export const ALLOWED_ATTACHMENT_TYPES = [
  ...ALLOWED_MEDIA_TYPES,
  ...ALLOWED_DOCUMENT_TYPES,
] as const;

const EXTENSION_MIME: Record<string, string> = {
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
  ".png": "image/png",
  ".webp": "image/webp",
  ".gif": "image/gif",
  ".mp4": "video/mp4",
  ".webm": "video/webm",
  ".mov": "video/quicktime",
  ".pdf": "application/pdf",
  ".doc": "application/msword",
  ".docx": "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ".ppt": "application/vnd.ms-powerpoint",
  ".pptx": "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  ".xls": "application/vnd.ms-excel",
  ".xlsx": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ".odt": "application/vnd.oasis.opendocument.text",
  ".ods": "application/vnd.oasis.opendocument.spreadsheet",
  ".odp": "application/vnd.oasis.opendocument.presentation",
  ".txt": "text/plain",
  ".rtf": "application/rtf",
};

export type ReactionType = "like" | "useful" | "interesting" | "agree";
export type PostAudience = "global" | "community";
export type FeedFilter = "all" | "question" | "poll" | "community";
export type PostType =
  | "text"
  | "image"
  | "video"
  | "document"
  | "link"
  | "poll"
  | "event"
  | "question";
export type AttachmentType = "image" | "video" | "document";

export const REACTION_OPTIONS: {
  type: ReactionType;
  label: string;
}[] = [
  { type: "like", label: "Tetszik" },
  { type: "useful", label: "Hasznos" },
  { type: "interesting", label: "Érdekes" },
  { type: "agree", label: "Egyetértek" },
];

export type CurrentUser = {
  id: string;
  fullName: string;
  professionalTitle: string | null;
  occupation: string | null;
  avatarPath: string | null;
  accountStatus: "pending" | "active" | "suspended" | "banned" | "anonymized";
  onboardingCompleted: boolean;
  introductionServicesViewed: boolean;
  introductionSettingsCompleted: boolean;
  introductionSkipped: boolean;
  createdAt: string;
  introductionExpired: boolean;
};

export type FeedAuthor = {
  id: string;
  fullName: string;
  professionalTitle: string | null;
  occupation: string | null;
  practiceId: string | null;
  practiceName: string | null;
  avatarPath: string | null;
};

export type FeedCommunity = {
  id: string;
  name: string;
  slug: string;
  memberCount?: number;
};

export type FeedAttachment = {
  id: string;
  type: AttachmentType;
  bucket: string | null;
  path: string | null;
  fileName: string | null;
  mimeType: string | null;
  sizeBytes: number | null;
  url: string | null;
};

export type PollOption = {
  id: string;
  label: string;
};

export type CreatePollInput = {
  options: PollOption[];
  allowOther: boolean;
  allowMultiple: boolean;
};

export type FeedPoll = CreatePollInput & {
  totalVoters: number;
  optionCounts: Record<string, number>;
  otherCount: number;
  selectedOptionIds: string[];
  selectedOtherText: string | null;
};

export type ReactionCounts = Record<ReactionType, number>;

export type FeedPost = {
  id: string;
  content: string | null;
  audience: PostAudience | "practice";
  postType: PostType;
  commentsEnabled: boolean;
  isPinned: boolean;
  isHidden: boolean;
  publishedAt: string;
  editedAt: string | null;
  commentCount: number;
  author: FeedAuthor;
  communities: FeedCommunity[];
  attachments: FeedAttachment[];
  reactionCounts: ReactionCounts;
  myReaction: ReactionType | null;
  isSaved: boolean;
  poll: FeedPoll | null;
  linkPreview: LinkPreview | null;
  sharedEventId: string | null;
  sharedEvent: EventSummary | null;
};

export type FeedCursor = {
  rank: number;
  at: string;
  id: string;
};

export type FeedPage = {
  posts: FeedPost[];
  nextCursor: FeedCursor | null;
};

export type HomeUnseenPostCounts = {
  questions: number;
  communityPosts: number;
};

export type FeedComment = {
  id: string;
  postId: string;
  parentCommentId: string | null;
  content: string;
  createdAt: string;
  author: Pick<FeedAuthor, "id" | "fullName" | "professionalTitle" | "avatarPath">;
};

export type CreatePostAttachmentInput = {
  attachment_type: AttachmentType;
  storage_bucket: string;
  storage_path: string;
  file_name: string;
  mime_type: string;
  size_bytes: number;
};

export function emptyReactionCounts(): ReactionCounts {
  return {
    like: 0,
    useful: 0,
    interesting: 0,
    agree: 0,
  };
}

export function isReactionType(value: string): value is ReactionType {
  return REACTION_OPTIONS.some((option) => option.type === value);
}

export function fileExtension(fileName: string) {
  const index = fileName.lastIndexOf(".");
  return index >= 0 ? fileName.slice(index).toLowerCase() : "";
}

export function resolvedMimeType(file: Pick<File, "name" | "type">) {
  if (file.type && file.type !== "application/octet-stream") {
    return file.type;
  }

  return EXTENSION_MIME[fileExtension(file.name)] ?? file.type;
}

function isListedType(
  value: string,
  list: readonly string[],
): boolean {
  return list.includes(value);
}

export function isAllowedAttachmentType(value: string) {
  return isListedType(value, ALLOWED_ATTACHMENT_TYPES);
}

export function isAllowedMediaType(file: Pick<File, "name" | "type">) {
  const mime = resolvedMimeType(file);
  return (
    isListedType(mime, ALLOWED_MEDIA_TYPES) ||
    isListedType(EXTENSION_MIME[fileExtension(file.name)] ?? "", ALLOWED_MEDIA_TYPES)
  );
}

export function isAllowedDocumentType(file: Pick<File, "name" | "type">) {
  const mime = resolvedMimeType(file);
  return (
    isListedType(mime, ALLOWED_DOCUMENT_TYPES) ||
    isListedType(EXTENSION_MIME[fileExtension(file.name)] ?? "", ALLOWED_DOCUMENT_TYPES)
  );
}

export function attachmentTypeFromMime(mimeType: string): AttachmentType {
  if (isListedType(mimeType, ALLOWED_IMAGE_TYPES) || mimeType.startsWith("image/")) {
    return "image";
  }

  if (isListedType(mimeType, ALLOWED_VIDEO_TYPES) || mimeType.startsWith("video/")) {
    return "video";
  }

  return "document";
}

export function postTypeFromAttachments(
  attachments: { type: AttachmentType }[],
): PostType {
  if (attachments.length === 0) {
    return "text";
  }

  if (attachments.some((attachment) => attachment.type === "video")) {
    return "video";
  }

  if (attachments.every((attachment) => attachment.type === "image")) {
    return "image";
  }

  return "document";
}

export function acceptAttribute(types: readonly string[], extensions: string[]) {
  return [...types, ...extensions].join(",");
}
